import React, { useCallback, useState } from 'react'

import {
  LaunchSketchAlert,
  NotFoundView,
  routes,
  useFlag,
  useUserSignedIn,
  useLicenseAgreement,
} from '@sketch/modules-common'

import { useDuplicateShareToDrafts } from '../../../shares/hooks/useDuplicateShareToDrafts'

import { ReactComponent as Calendar } from '@sketch/icons/calendar-17'
import { ReactComponent as Star } from '@sketch/icons/star-17'
import { ReactComponent as MacIcon } from '@sketch/icons/mac-app-logo'
import { ReactComponent as Document } from '@sketch/icons/document-17'
import { ReactComponent as Library } from '@sketch/icons/link-library-17'
import { ReactComponent as Template } from '@sketch/icons/templates-17'
import { ReactComponent as Library24 } from '@sketch/icons/link-library-24'
import { ReactComponent as EllipsisIcon } from '@sketch/icons/dots-3-horizontal-24'

import ReportShareModal from '../../../shares/components/ReportShareModal'

import {
  Button,
  useModalContext,
  useForTablet,
  useResponsiveDropdown,
  downloadFile,
  useForDesktop,
} from '@sketch/components'

import { PublicationActionsDropdown } from '../../components/PublicationActionsDropdown'

import { ProfileAvatar } from '../../components/ProfileAvatar'
import MoreFromThisCreator from '../../components/MoreFromThisCreator'
import PublicationCanvas from '../../components/PublicationCanvas/PublicationCanvas'
import HeartButton from '../../components/HeartButton/HeartButton'
import CommunityHeader from '../../components/CommunityHeader'
import PublicViewHiddenProfileBanner from '../../components/PublicViewHiddenProfileBanner'

import {
  Wrapper,
  Content,
  Title,
  DescriptionWrapper,
  MaxWidth,
  Detail,
  Icon,
  StyledButton,
  TitleContainer,
  DetailsContainer,
  ActionsWrapper,
  NoImageThumbnail,
  NoImageIcon,
  MacApp,
  VersionDescription,
  DocumentTypes,
  StyledTimeAgo,
  DropdownButton,
  StyledEditableDocumentDescription,
  OpenInSketchButton,
} from './WorkspaceProfileDocument.styles'

import {
  InitialUserFragment,
  PublicationFragment,
  PublicationPageFragment,
  useGetSharePagesQuery,
  useShareDescriptionChangeMutation,
} from '@sketch/gql-types'
import { useCanDuplicateToWorkspace } from '../../../workspace/hooks'

interface WorkspaceProfileDocumentProps {
  publication: PublicationFragment
  user?: Pick<InitialUserFragment, 'name' | 'avatar'>
  shortUrlName: string
}

type SelectedPage = PublicationPageFragment | null

interface ActionsContainerProps {
  publication: PublicationFragment
  workspaceIdentifier: string
  versionIdentifier: string
}

const ActionsContainer: React.FCC<ActionsContainerProps> = ({
  workspaceIdentifier,
  versionIdentifier,
  publication,
}) => {
  const { share } = publication
  const { showModal } = useModalContext()
  const isTabletAndBigger = useForTablet()
  const isDesktop = useForDesktop()
  const isSignedIn = useUserSignedIn()

  const { showLicenseAgreementModal } = useLicenseAgreement({
    shareIdentifier: share.identifier,
  })

  const [duplicateShare, { loading }] = useDuplicateShareToDrafts({
    share,
    versionIdentifier,
    workspaceIdentifier,
  })

  const isPublicationHeartsEnabled = useFlag('publication-hearts')
  const showHeartButton = isPublicationHeartsEnabled

  const { data, loading: canDuplicateLoading } = useCanDuplicateToWorkspace(
    share.workspace.identifier,
    share.publicAccessLevel,
    share.userCanInspect
  )
  const canDuplicate = data?.canDuplicate

  const isMobile = !isTabletAndBigger
  const isLibrary = share.shareType === 'LIBRARY'
  const isTemplate = share.shareType === 'TEMPLATE'
  const isAdmin = share.workspace.userRole === 'ADMIN'

  const isUserAbleToOpenInSketch = share.userCanEdit
  const isDownloadAvailable = share.version?.document?.downloadAvailable

  const downloadUrl = share.version?.document?.url

  const [dropdown, buttonProps] = useResponsiveDropdown({
    dropdown: PublicationActionsDropdown,
    dropdownProps: {
      publication,
      workspace: share.workspace,
      onDuplicate: duplicateShare,
      isDuplicating: loading,
    },
    placement: 'bottom-end',
    usePortal: true,
  })

  const renderAdminDropdownButton = () => (
    <DropdownButton {...buttonProps}>
      <EllipsisIcon width="24" height="24" />
    </DropdownButton>
  )

  const renderLibraryButton = () => (
    <Button
      variant="tertiary"
      icon={() => <Library24 width="17" height="17" />}
      onClick={() =>
        showLicenseAgreementModal({
          helpText:
            'Read and agree to the License Agreement to add this Library.',
          agreeAction: () =>
            showModal(LaunchSketchAlert, {
              shareId: share.identifier,
              action: 'addLibrary',
            }),
        })
      }
      size="40"
    >
      Add Library
    </Button>
  )

  const renderDuplicateButton = () => (
    <Button
      variant="tertiary"
      icon={() =>
        isTemplate ? (
          <Template width="17" height="17" />
        ) : (
          <Document width="17" height="17" />
        )
      }
      onClick={() =>
        showLicenseAgreementModal({
          helpText:
            'Read and agree to the License Agreement to remix this Document.',
          agreeAction: duplicateShare,
        })
      }
      size="40"
      loading={loading || canDuplicateLoading}
    >
      {isTemplate ? 'Add Template' : 'Duplicate'}
    </Button>
  )

  const renderNonAdminActions = () => {
    if (isLibrary && !isMobile) {
      return renderLibraryButton()
    }

    if (canDuplicate && !isLibrary) {
      return renderDuplicateButton()
    }

    return null
  }

  return (
    <ActionsWrapper data-testid="publication-actions">
      {showHeartButton && (
        <HeartButton publication={publication} trackHeartCount />
      )}
      {isAdmin ? renderAdminDropdownButton() : renderNonAdminActions()}
      {dropdown}
      {!isAdmin && isDownloadAvailable && downloadUrl && (
        <Button
          variant="tertiary"
          size="40"
          onClick={() =>
            showLicenseAgreementModal({
              helpText:
                'Read and agree to the License Agreement to download this Document.',
              agreeAction: () => {
                downloadFile(downloadUrl)
              },
            })
          }
        >
          Download
        </Button>
      )}
      {(isUserAbleToOpenInSketch || !isSignedIn) && isDesktop && (
        <OpenInSketchButton
          variant="primary-untinted"
          size="40"
          onClick={() => {
            showModal(LaunchSketchAlert, {
              action: 'openInSketch',
              shareId: share.identifier,
            })
          }}
        >
          <MacApp as={MacIcon} /> Open in Sketch
        </OpenInSketchButton>
      )}
    </ActionsWrapper>
  )
}

const WorkspaceProfileDocument: React.FCC<
  WorkspaceProfileDocumentProps
> = props => {
  const { user, publication, shortUrlName } = props

  const [selectedPage, setSelectedPage] = useState<SelectedPage>(null)

  const { showModal } = useModalContext()

  const share = publication.share
  const { name, description, identifier, version } = share

  const workspace = share.workspace
  const profile = workspace.profile
  const isAdmin = workspace.userRole === 'ADMIN'
  const canUpdateDescription = share.userCanUpdateSettings
  const isProfilePublic = profile?.isPublic === true
  const isAvailableToAnonymousUsers = !(!isAdmin && !isProfilePublic)
  const showHiddenProfileBanner = isAdmin && !isProfilePublic

  // Add description editing capability
  const [changeDocumentDescription] = useShareDescriptionChangeMutation({
    onError: 'show-toast',
    redirectErrors: true,
    optimisticResponse: ({ identifier, description }) => ({
      __typename: 'RootMutationType',
      shareUpdate: {
        __typename: 'ShareUpdatePayload',
        successful: true,
        share: {
          __typename: 'Share',
          identifier,
          description,
          extDescription: {
            __typename: 'PlainText',
            contents: description,
          },
        },
        errors: [],
      },
    }),
  })

  const descriptionEditHandler = (newDescription: string) => {
    changeDocumentDescription({
      variables: { identifier: share.identifier, description: newDescription },
    })
  }

  // Loading and Error is dealt with inside PublicationPagesDropdown
  useGetSharePagesQuery({
    variables: {
      identifier: share.identifier,
    },
  })

  const handleClick = () => {
    showModal(ReportShareModal, {
      identifier,
    })
  }

  const handlePageSelected = useCallback((page: PublicationPageFragment) => {
    setSelectedPage(page)
  }, [])

  // Profile and Share should always be available
  if (!profile || !share || !version || !isAvailableToAnonymousUsers) {
    return <NotFoundView />
  }

  // Grab first and only page from entries array
  const [firstPage] = version?.document?.pages?.entries ?? []

  const updatedAt = version?.updatedAt ?? publication.updatedAt

  const page = selectedPage ?? firstPage
  const hasPresentationFile = !!page?.presentationFile

  const isLibrary = share.shareType === 'LIBRARY'
  const isTemplate = share.shareType === 'TEMPLATE'
  const isDocument = share.shareType === 'STANDARD'
  const isStarred = share.version?.kind === 'PUBLISHED'
  const shareDescription = description ?? ''
  const versionDescription = share.version?.description ?? ''
  const isOfficial = !!publication.workspaceProfile?.isOfficial

  return (
    <>
      {showHiddenProfileBanner && (
        <PublicViewHiddenProfileBanner workspaceId={workspace.identifier} />
      )}

      <CommunityHeader user={user} />

      <Wrapper>
        <MaxWidth $width={1280}>
          <ProfileAvatar
            workspaceName={workspace.name}
            avatar={workspace.avatar}
            to={routes.WORKSPACE_PROFILE.create({
              shortUrlName,
            })}
            isOfficial={isOfficial}
            isSmall
          />
          <TitleContainer>
            <Title>{name}</Title>
            <ActionsContainer
              publication={publication}
              workspaceIdentifier={workspace.identifier}
              versionIdentifier={version.identifier}
            />
          </TitleContainer>
          {hasPresentationFile ? (
            <PublicationCanvas
              versionShortId={version.shortId}
              shareIdentifier={share.identifier}
              page={page}
              onPageSelected={handlePageSelected}
            />
          ) : (
            <NoImageThumbnail>
              <NoImageIcon aria-label="No Image available" />
            </NoImageThumbnail>
          )}
          <MaxWidth $width={840}>
            <Content>
              <DescriptionWrapper>
                <StyledEditableDocumentDescription
                  hideIcon
                  placeholder="Write a description for this document…"
                  description={shareDescription}
                  editHandler={
                    canUpdateDescription ? descriptionEditHandler : undefined
                  }
                  canUpdateDescription={canUpdateDescription}
                />
              </DescriptionWrapper>
              <DetailsContainer>
                <DocumentTypes>
                  {isLibrary && (
                    <Detail>
                      <Icon as={Library} /> Library
                    </Detail>
                  )}
                  {isTemplate && (
                    <Detail>
                      <Icon as={Template} /> Template
                    </Detail>
                  )}
                  {isDocument && (
                    <Detail>
                      <Icon as={Document} /> Document
                    </Detail>
                  )}
                  <Detail>
                    <Icon as={isStarred ? Star : Calendar} /> Updated
                    <StyledTimeAgo date={updatedAt} />
                  </Detail>
                </DocumentTypes>
                <Detail>
                  <VersionDescription>{versionDescription}</VersionDescription>
                </Detail>
                <StyledButton onClick={handleClick}>Report</StyledButton>
              </DetailsContainer>
            </Content>
          </MaxWidth>
          <MoreFromThisCreator
            workspaceIdentifier={workspace.identifier}
            creatorName={workspace.name}
            exclude={publication.identifier}
          />
        </MaxWidth>
      </Wrapper>
    </>
  )
}

export default WorkspaceProfileDocument
